import React, { useEffect, useState, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDropzone } from 'react-dropzone'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  Chip,
  LinearProgress,
  FormControlLabel,
  Switch,
  CircularProgress,
  Tooltip
} from '@mui/material'
import {
  Close,
  ShoppingCart,
  Business,
  CalendarToday,
  Description,
  AttachMoney,
  CloudUpload,
  PictureAsPdf,
  Delete,
  Repeat,
  Fullscreen
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import { useCreatePurchaseOrder, useUpdatePurchaseOrder } from '../../hooks/usePurchaseOrders'
import { useClients } from '../../hooks/useClients'
import { formatCurrency } from '../../utils/formatters'
import api from '../../utils/api'

// Validation schema
const purchaseOrderSchema = yup.object({
  ocNumber: yup
    .string()
    .required('El número de OC es requerido'),
  clientId: yup
    .string()
    .required('Debe seleccionar un cliente'),
  issueDate: yup
    .date()
    .required('La fecha de emisión es requerida'),
  description: yup
    .string()
    .required('La descripción es requerida')
    .min(1, 'La descripción no puede estar vacía')
    .max(1000, 'La descripción no puede exceder 1000 caracteres'),
  currencyType: yup
    .string()
    .oneOf(['PESOS', 'UF'])
    .required('Debe seleccionar el tipo de moneda'),
  netAmount: yup
    .number()
    .when('currencyType', {
      is: 'PESOS',
      then: (schema) => schema.required('El monto neto es requerido').positive('El monto debe ser positivo'),
      otherwise: (schema) => schema.nullable()
    }),
  netAmountUF: yup
    .number()
    .when('currencyType', {
      is: 'UF',
      then: (schema) => schema.required('El monto neto en UF es requerido').positive('El monto neto en UF debe ser positivo'),
      otherwise: (schema) => schema.nullable()
    }),
  ufValue: yup
    .number()
    .when('currencyType', {
      is: 'UF',
      then: (schema) => schema.required('El valor de la UF es requerido').positive('El valor de la UF debe ser positivo'),
      otherwise: (schema) => schema.nullable()
    }),
  ivaPercentage: yup
    .number()
    .min(0, 'El IVA no puede ser negativo')
    .max(100, 'El IVA no puede exceder 100%')
    .default(19),
  ocType: yup
    .string()
    .oneOf(['UNICA', 'PERIODICA'])
    .required('Debe seleccionar un tipo de OC'),
  isPeriodic: yup
    .boolean()
    .default(false),
  billingDay: yup
    .number()
    .integer()
    .min(1, 'El día debe estar entre 1 y 31')
    .max(31, 'El día debe estar entre 1 y 31')
    .when('isPeriodic', {
      is: true,
      then: (schema) => schema.required('El día de facturación es requerido para OC periódicas'),
      otherwise: (schema) => schema.nullable()
    })
})

const PurchaseOrderForm = ({ 
  open, 
  onClose, 
  purchaseOrder = null, 
  title = null 
}) => {
  const isEdit = !!purchaseOrder
  const [pdfFile, setPdfFile] = useState(null)
  const [pdfPreview, setPdfPreview] = useState(null)
  const [isExtracting, setIsExtracting] = useState(false)
  const [extractionError, setExtractionError] = useState(null)
  const [extractionSuccess, setExtractionSuccess] = useState(false)
  
  const createPurchaseOrderMutation = useCreatePurchaseOrder()
  const updatePurchaseOrderMutation = useUpdatePurchaseOrder()
  
  // Get clients for dropdown
  const { data: clientsData, refetch: refetchClients } = useClients({ page: 1, limit: 100 })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    control
  } = useForm({
    resolver: yupResolver(purchaseOrderSchema),
    defaultValues: {
      ocNumber: '',
      clientId: '',
      issueDate: dayjs(),
      description: '',
      currencyType: 'PESOS',
      netAmount: '',
      netAmountUF: null,
      ufValue: null,
      ivaPercentage: 19,
      ocType: 'UNICA',
      isPeriodic: false,
      billingDay: null
    }
  })

  // Watch values for calculations and conditional logic
  const currencyType = watch('currencyType')
  const netAmount = watch('netAmount')
  const netAmountUF = watch('netAmountUF')
  const ufValue = watch('ufValue')
  const ivaPercentage = watch('ivaPercentage')
  const ocType = watch('ocType')
  const isPeriodic = watch('isPeriodic')

  // Calculate amounts - support UF to peso conversion
  const calculatedAmounts = React.useMemo(() => {
    let finalNetAmount = 0
    
    // Calculate based on currency type
    if (currencyType === 'UF' && netAmountUF && ufValue) {
      finalNetAmount = Math.round(parseFloat(netAmountUF) * parseFloat(ufValue))
      // Auto-update netAmount field with calculated pesos value
      setValue('netAmount', finalNetAmount)
    } else if (currencyType === 'PESOS' && netAmount) {
      // Use regular peso amount
      finalNetAmount = Math.round(parseFloat(netAmount) || 0)
    }
    
    const ivaPercent = parseFloat(ivaPercentage) || 0
    const ivaAmount = Math.round(finalNetAmount * (ivaPercent / 100))
    const totalAmount = finalNetAmount + ivaAmount
    
    return {
      netAmount: finalNetAmount,
      ivaAmount: ivaAmount,
      totalAmount: totalAmount
    }
  }, [currencyType, netAmount, netAmountUF, ufValue, ivaPercentage, setValue])

  // Update form values when purchase order changes
  useEffect(() => {
    if (purchaseOrder) {
      setValue('ocNumber', purchaseOrder.ocNumber || '')
      setValue('clientId', purchaseOrder.clientId || '')
      setValue('issueDate', dayjs(purchaseOrder.issueDate))
      setValue('description', purchaseOrder.description || '')
      // Determine currency type based on existing data
      const hasUFData = purchaseOrder.netAmountUF && purchaseOrder.ufValue
      setValue('currencyType', hasUFData ? 'UF' : 'PESOS')
      setValue('netAmount', parseFloat(purchaseOrder.netAmount) || '')
      setValue('netAmountUF', purchaseOrder.netAmountUF ? parseFloat(purchaseOrder.netAmountUF) : null)
      setValue('ufValue', purchaseOrder.ufValue ? parseFloat(purchaseOrder.ufValue) : null)
      setValue('ivaPercentage', parseFloat(purchaseOrder.ivaPercentage) || 19)
      setValue('ocType', purchaseOrder.ocType || 'UNICA')
      setValue('isPeriodic', purchaseOrder.isPeriodic || false)
      setValue('billingDay', purchaseOrder.billingDay || null)
      
      // Set PDF preview if exists
      if (purchaseOrder.pdfUrl) {
        setPdfPreview(purchaseOrder.pdfUrl)
      }
    } else {
      reset({
        ocNumber: '',
        clientId: '',
        issueDate: dayjs(),
        description: '',
        currencyType: 'PESOS',
        netAmount: '',
        netAmountUF: null,
        ufValue: null,
        ivaPercentage: 19,
        ocType: 'UNICA',
        isPeriodic: false,
        billingDay: null
      })
      setPdfFile(null)
      setPdfPreview(null)
    }
  }, [purchaseOrder, setValue, reset])

  // Clear extraction states when modal opens/closes
  useEffect(() => {
    if (open) {
      setExtractionError(null)
      setExtractionSuccess(false)
      setIsExtracting(false)
    }
  }, [open])

  // Auto-update isPeriodic when ocType changes
  useEffect(() => {
    if (ocType === 'PERIODICA') {
      setValue('isPeriodic', true)
    } else {
      setValue('isPeriodic', false)
      setValue('billingDay', null)
    }
  }, [ocType, setValue])

  const isLoading = createPurchaseOrderMutation.isLoading || updatePurchaseOrderMutation.isLoading

  const onSubmit = async (data) => {
    try {
      const formData = new FormData()
      
      // Add form fields
      formData.append('ocNumber', data.ocNumber)
      formData.append('clientId', data.clientId)
      formData.append('issueDate', data.issueDate.toISOString())
      formData.append('description', data.description)
      formData.append('currencyType', data.currencyType)
      
      // Add amount fields based on currency type
      if (data.currencyType === 'PESOS') {
        formData.append('netAmount', data.netAmount)
      } else if (data.currencyType === 'UF') {
        formData.append('netAmountUF', data.netAmountUF)
        formData.append('ufValue', data.ufValue)
      }
      formData.append('ivaPercentage', data.ivaPercentage)
      formData.append('ocType', data.ocType)
      formData.append('isPeriodic', data.isPeriodic.toString())
      
      // Add billing day only if periodic
      if (data.isPeriodic && data.billingDay) {
        formData.append('billingDay', data.billingDay.toString())
      }
      
      // Add PDF file if selected
      if (pdfFile) {
        formData.append('pdf', pdfFile)
      }

      if (isEdit) {
        await updatePurchaseOrderMutation.mutateAsync({
          id: purchaseOrder.id,
          formData
        })
      } else {
        await createPurchaseOrderMutation.mutateAsync(formData)
      }
      
      handleClose()
    } catch (error) {
      console.error('Error saving purchase order:', error)
    }
  }

  const handleClose = () => {
    reset()
    setPdfFile(null)
    setPdfPreview(null)
    setExtractionError(null)
    setExtractionSuccess(false)
    setIsExtracting(false)
    onClose()
  }

  // PDF Upload handling
  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0]
    if (file) {
      setPdfFile(file)
      setPdfPreview(URL.createObjectURL(file))
      
      // Auto-extract data from PDF
      await extractDataFromPDF(file)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  })

  const handleRemovePdf = () => {
    setPdfFile(null)
    setPdfPreview(null)
  }

  const extractDataFromPDF = async (file) => {
    try {
      setIsExtracting(true)
      setExtractionError(null)

      const formData = new FormData()
      formData.append('pdf', file)

      const response = await api.post('/purchase-orders/extract', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      console.log('API Response:', response.config)
      console.log('API Status:', response.status)
      console.log('API Response Data:', response.data)
      
      const { extractedData } = response.data
      if (!extractedData) {
        throw new Error(response.data?.message || response.data?.error || 'No data extracted')
      }
      
      console.log('Extracted data:', extractedData)

      // Fill form with extracted data
      if (extractedData.purchaseOrderData) {
        const { purchaseOrderData, clientData, clientExists, existingClientId } = extractedData

        // Set purchase order data
        setValue('ocNumber', purchaseOrderData.ocNumber || '')
        
        // Parse and validate date
        const issueDate = purchaseOrderData.issueDate 
          ? (dayjs(purchaseOrderData.issueDate).isValid() 
              ? dayjs(purchaseOrderData.issueDate) 
              : dayjs())
          : dayjs()
        setValue('issueDate', issueDate)
        
        setValue('description', purchaseOrderData.description || '')
        // Determine currency type based on extracted data
        const hasUFData = purchaseOrderData.netAmountUF && purchaseOrderData.ufValue
        setValue('currencyType', hasUFData ? 'UF' : 'PESOS')
        setValue('netAmount', purchaseOrderData.netAmount && !isNaN(purchaseOrderData.netAmount) ? purchaseOrderData.netAmount : '')
        setValue('netAmountUF', purchaseOrderData.netAmountUF && !isNaN(purchaseOrderData.netAmountUF) ? purchaseOrderData.netAmountUF : null)
        setValue('ufValue', purchaseOrderData.ufValue && !isNaN(purchaseOrderData.ufValue) ? purchaseOrderData.ufValue : null)
        setValue('ivaPercentage', purchaseOrderData.ivaPercentage && !isNaN(purchaseOrderData.ivaPercentage) ? purchaseOrderData.ivaPercentage : 19)

        // Set OC type and periodic settings
        setValue('ocType', purchaseOrderData.ocType || 'UNICA')
        setValue('isPeriodic', purchaseOrderData.isPeriodic || false)
        if (purchaseOrderData.billingDay) {
          setValue('billingDay', purchaseOrderData.billingDay)
        }

        // Handle client - Same logic as InvoiceForm
        let selectedClient = null
        if (clientExists && existingClientId) {
          setValue('clientId', existingClientId)
          selectedClient = clientsData?.clients?.find(c => c.id === existingClientId)
        } else {
          // Double check in local data
          const localClient = findClientByRut(clientData.rut)
          if (localClient) {
            setValue('clientId', localClient.id)
            selectedClient = localClient
          } else {
            // Create new client automatically
            const newClientId = await createNewClient(clientData)
            if (newClientId) {
              setValue('clientId', newClientId)
              // Use default payment terms for new client
              selectedClient = { paymentTermsDays: 30 }
            }
          }
        }
        
        // Show success message
        setExtractionSuccess(true)
        setTimeout(() => setExtractionSuccess(false), 3000)
      }

    } catch (error) {
      console.error('Error extracting PDF data:', error)
      const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error || 
                           error.message || 
                           'Error extrayendo datos del PDF'
      setExtractionError(errorMessage)
    } finally {
      setIsExtracting(false)
    }
  }

  const findClientByRut = (rut) => {
    if (!rut || !clientsData?.clients) return null
    
    // Clean RUT by removing dots, dashes, and verification digit
    const cleanRut = (rutString) => {
      if (!rutString) return ''
      return rutString.replace(/[.-]/g, '').replace(/\d$/, '')
    }
    
    const cleanedRut = cleanRut(rut)
    return clientsData.clients.find(client => 
      client.rut && cleanRut(client.rut) === cleanedRut
    )
  }

  const createNewClient = async (clientData) => {
    try {
      // Mapear clientData a la estructura esperada por el backend
      const clientPayload = {
        name: clientData.name,
        rut: clientData.cleanRut || clientData.rut, // Usar cleanRut si está disponible
        email: clientData.email,
        phone: clientData.phone,
        address: clientData.address,
        paymentTermsDays: clientData.paymentTermsDays || 30
      }
      
      console.log('Creating client with payload:', clientPayload)
      
      const response = await api.post('/clients', clientPayload)
      const newClient = response.data.data // El backend devuelve { data: client }
      
      // Refresh clients data to include the new client
      await refetchClients()
      
      // Small delay to ensure data is updated
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Return the new client ID
      return newClient.id
      
    } catch (error) {
      console.error('Error creating client:', error)
      console.error('Error response:', error.response?.data)
      setExtractionError('Error creando cliente automáticamente: ' + (error.response?.data?.message || error.message))
      return null
    }
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, height: '90vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCart color="primary" />
            <Typography variant="h6">
              {title || (isEdit ? 'Editar Orden de Compra' : 'Nueva Orden de Compra')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Left Column - Form Fields */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={3}>
                {/* OC Number */}
                <Grid item xs={12} md={6}>
                  <TextField
                    {...register('ocNumber')}
                    fullWidth
                    label="Número de OC"
                    placeholder="OC-001-2024"
                    error={!!errors.ocNumber}
                    helperText={errors.ocNumber?.message}
                    disabled={isLoading}
                  />
                </Grid>

                {/* Client */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.clientId}>
                    <InputLabel>Cliente</InputLabel>
                    <Controller
                      name="clientId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Cliente"
                          disabled={isLoading}
                        >
                          {clientsData?.clients?.map((client) => (
                            <MenuItem key={client.id} value={client.id}>
                              {client.name} ({client.rut})
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.clientId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.clientId.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Issue Date */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="issueDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        label="Fecha de Emisión"
                        disabled={isLoading}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.issueDate,
                            helperText: errors.issueDate?.message
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* OC Type */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.ocType}>
                    <InputLabel>Tipo de OC</InputLabel>
                    <Controller
                      name="ocType"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Tipo de OC"
                          disabled={isLoading}
                        >
                          <MenuItem value="UNICA">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ShoppingCart fontSize="small" />
                              Única
                            </Box>
                          </MenuItem>
                          <MenuItem value="PERIODICA">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Repeat fontSize="small" />
                              Periódica
                            </Box>
                          </MenuItem>
                        </Select>
                      )}
                    />
                    {errors.ocType && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.ocType.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Billing Day - Only show for periodic */}
                {isPeriodic && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      {...register('billingDay')}
                      fullWidth
                      label="Día de Facturación"
                      type="number"
                      inputProps={{ min: 1, max: 31 }}
                      error={!!errors.billingDay}
                      helperText={errors.billingDay?.message || 'Día del mes para generar facturas (1-31)'}
                      disabled={isLoading}
                    />
                  </Grid>
                )}

                {/* Description */}
                <Grid item xs={12}>
                  <TextField
                    {...register('description')}
                    fullWidth
                    label="Descripción"
                    multiline
                    rows={3}
                    placeholder="Describe los productos o servicios de la orden de compra..."
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    disabled={isLoading}
                  />
                </Grid>

                {/* Currency Type */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.currencyType}>
                    <InputLabel>Tipo de Moneda</InputLabel>
                    <Controller
                      name="currencyType"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Tipo de Moneda"
                          disabled={isLoading}
                        >
                          <MenuItem value="PESOS">Pesos Chilenos</MenuItem>
                          <MenuItem value="UF">Unidad de Fomento (UF)</MenuItem>
                        </Select>
                      )}
                    />
                    {errors.currencyType && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {errors.currencyType.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Net Amount - Conditional based on currency type */}
                {currencyType === 'PESOS' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      {...register('netAmount')}
                      fullWidth
                      label="Monto Neto (Pesos)"
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      error={!!errors.netAmount}
                      helperText={errors.netAmount?.message}
                      disabled={isLoading}
                      InputProps={{
                        startAdornment: <AttachMoney sx={{ mr: 1, color: 'action.active' }} />
                      }}
                    />
                  </Grid>
                )}

                {/* UF Support - Only show when currency type is UF */}
                {currencyType === 'UF' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        {...register('netAmountUF')}
                        fullWidth
                        label="Monto Neto en UF"
                        type="number"
                        inputProps={{ min: 0, step: 0.000001 }}
                        error={!!errors.netAmountUF}
                        helperText={errors.netAmountUF?.message}
                        disabled={isLoading}
                        placeholder="Ej: 0.025000"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        {...register('ufValue')}
                        fullWidth
                        label="Valor UF del día"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        error={!!errors.ufValue}
                        helperText={errors.ufValue?.message || 'Valor de la UF con 2 decimales'}
                        disabled={isLoading}
                        placeholder="Ej: 37000.00"
                      />
                    </Grid>
                  </>
                )}

                {/* IVA Percentage */}
                <Grid item xs={12} md={6}>
                  <TextField
                    {...register('ivaPercentage')}
                    fullWidth
                    label="IVA (%)"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    error={!!errors.ivaPercentage}
                    helperText={errors.ivaPercentage?.message}
                    disabled={isLoading}
                  />
                </Grid>

                {/* Calculated Amounts */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: 'background.default' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Cálculo de Montos
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Neto:
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {formatCurrency(calculatedAmounts.netAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          IVA ({ivaPercentage}%):
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {formatCurrency(calculatedAmounts.ivaAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Total:
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="primary.main">
                          {formatCurrency(calculatedAmounts.totalAmount)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* OC Type Info */}
                <Grid item xs={12}>
                  <Alert 
                    severity={isPeriodic ? "info" : "success"} 
                    icon={isPeriodic ? <Repeat /> : <ShoppingCart />}
                  >
                    <Typography variant="body2">
                      <strong>
                        {isPeriodic ? 'Orden de Compra Periódica' : 'Orden de Compra Única'}
                      </strong>
                    </Typography>
                    <Typography variant="caption" display="block">
                      {isPeriodic 
                        ? 'Esta OC generará facturas automáticamente cada mes en el día especificado.'
                        : 'Esta OC se utilizará para generar una sola factura.'
                      }
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </Grid>

            {/* Right Column - PDF Upload */}
            <Grid item xs={12} md={5}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Archivo PDF
                </Typography>
                
                {/* PDF Upload Area */}
                <Paper
                  {...getRootProps()}
                  sx={{
                    p: 3,
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'divider',
                    backgroundColor: isDragActive ? 'action.hover' : 'background.default',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    mb: 2,
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <input {...getInputProps()} />
                  {isExtracting ? (
                    <>
                      <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h6" gutterBottom color="primary.main">
                        Extrayendo datos con IA...
                      </Typography>
                      <LinearProgress sx={{ width: '80%', mx: 'auto', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Analizando PDF con OpenAI
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        {isDragActive ? 'Suelta el archivo aquí' : 'Arrastra o haz clic'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Archivo PDF (máx. 10MB)
                      </Typography>
                      <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>
                        ✨ Extracción automática con IA
                      </Typography>
                    </>
                  )}
                </Paper>

                {/* PDF Preview */}
                {(pdfFile || pdfPreview) && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PictureAsPdf color="error" />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {pdfFile ? pdfFile.name : 'PDF existente'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pdfFile ? `${(pdfFile.size / 1024 / 1024).toFixed(2)} MB` : 'Archivo actual'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Ver en pantalla completa">
                          <IconButton 
                            size="small" 
                            onClick={() => window.open(pdfPreview, '_blank')}
                          >
                            <Fullscreen />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={handleRemovePdf}>
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    {/* PDF Preview Viewer */}
                    <Box sx={{ 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 1,
                      overflow: 'hidden',
                      backgroundColor: '#f5f5f5'
                    }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: '400px',
                          overflow: 'hidden',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          p: 1
                        }}
                      >
                        <iframe
                          src={`${pdfPreview}#toolbar=0&navpanes=0&scrollbar=1&zoom=100`}
                          style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            backgroundColor: 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            borderRadius: '4px'
                          }}
                          title="PDF Preview"
                          onLoad={() => console.log('PDF Preview loaded')}
                        />
                      </Box>
                    </Box>
                  </Paper>
                )}

                {/* Upload Progress */}
                {isLoading && (pdfFile || isEdit) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {isEdit ? 'Actualizando orden de compra...' : 'Subiendo archivo...'}
                    </Typography>
                    <LinearProgress />
                  </Box>
                )}

                {/* AI Extraction Status */}
                {isExtracting && (
                  <Box sx={{ mb: 2 }}>
                    <Alert severity="info" variant="outlined">
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        Extrayendo datos con IA...
                      </Typography>
                    </Alert>
                  </Box>
                )}

                {extractionError && (
                  <Box sx={{ mb: 2 }}>
                    <Alert severity="error" variant="outlined">
                      <Typography variant="body2">
                        {extractionError}
                      </Typography>
                    </Alert>
                  </Box>
                )}

                {extractionSuccess && (
                  <Box sx={{ mb: 2 }}>
                    <Alert severity="success" variant="outlined">
                      <Typography variant="body2">
                        ✨ Datos extraídos automáticamente con IA
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Revisa los campos y ajusta si es necesario.
                      </Typography>
                    </Alert>
                  </Box>
                )}

                {/* PDF Info */}
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Información:</strong><br />
                    • Solo archivos PDF<br />
                    • Tamaño máximo: 10MB<br />
                    • Se almacena en Amazon S3<br />
                    • URLs firmadas para seguridad
                  </Typography>
                </Alert>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleClose}
            disabled={isLoading}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            sx={{ minWidth: 140 }}
          >
            {isLoading 
              ? (isEdit ? 'Actualizando...' : 'Creando...') 
              : (isEdit ? 'Actualizar OC' : 'Crear OC')
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default PurchaseOrderForm
